import { Suspense } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../components/common/EmptyState/EmptyState';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { Pagination } from '../../../components/common/Pagination';
import { MobileBottomNav } from '../Stations/MobileBottomNav';
import { MobileListHeader } from './MobileListHeader';
import type { ListPageSearchProps } from './MobileListHeader';
import { SectionTabs } from './SectionTabs';
import type { DashboardSection } from '../../../layouts/DashboardV2Layout/dashboardSections';

export interface ListPagePaginationProps {
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

export interface ListPageEmptyStateProps {
  /** Material Symbols icon name. */
  icon: string;
  message: string;
}

export interface ListPageFilterButtonProps {
  activeCount: number;
  onOpen: () => void;
}

interface ListPageLayoutProps {
  /** The tabbed section this page belongs to; its tabs head the page on both layouts. */
  section?: DashboardSection;
  /** Desktop filter bar above the content area; dimmed while a refetch is in flight. */
  filterBar?: ReactNode;
  /**
   * Desktop toolbar inside the content area (search + count + primary
   * action — see `ListPageToolbar`); dimmed while a refetch is in flight.
   * Pages with a toolbar usually omit `countText` (the count lives in it).
   */
  desktopToolbar?: ReactNode;
  /** Dims the desktop filter bar / toolbar (typically `isLoading || isFetching`). */
  showLoading: boolean;
  /** Initial-load state: renders skeletons instead of content. */
  isLoading: boolean;
  hasError: boolean;
  errorMessage: string;
  onRetry: () => void;
  /** Desktop result-count line (already translated); omit if the toolbar shows it. */
  countText?: string;
  /** Items on the current page; 0 renders the empty state. */
  itemCount: number;
  emptyState: ListPageEmptyStateProps;
  table: ReactNode;
  tableSkeleton: ReactNode;
  mobileTitle: string;
  /** Optional action next to the mobile title (e.g. an add button). */
  mobileHeaderAction?: ReactNode;
  /** Mobile result-count line (already translated). */
  mobileCountText: string;
  search: ListPageSearchProps;
  mobileCards: ReactNode;
  mobileSkeleton: ReactNode;
  /** Mobile pagination; omit for unpaginated lists. */
  pagination?: ListPagePaginationProps;
  /** Mobile filter trigger; omit to hide the button. */
  filterButton?: ListPageFilterButtonProps;
  isFilterSheetOpen?: boolean;
  /** Lazy mobile filter sheet element; rendered under Suspense while open. */
  filterSheet?: ReactNode;
}

interface ListContentProps {
  hasError: boolean;
  errorMessage: string;
  onRetry: () => void;
  isLoading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
}

/** Shared error → skeleton → content branch for both layouts. */
export const ListContent = ({
  hasError,
  errorMessage,
  onRetry,
  isLoading,
  skeleton,
  children,
}: ListContentProps) => {
  if (hasError) {
    return <ErrorMessage message={errorMessage} onRetry={onRetry} />;
  }
  if (isLoading) {
    return <>{skeleton}</>;
  }
  return <>{children}</>;
};

const MobileFilterButton = ({ activeCount, onOpen }: ListPageFilterButtonProps) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`mobile-filter-btn${activeCount > 0 ? ' has-filters' : ''}`}
      onClick={onOpen}
      aria-label={
        activeCount > 0
          ? t('dashboard.stations.filterAriaWithCount', { count: activeCount })
          : t('dashboard.stations.filterAria')
      }
    >
      <span className="material-symbols-outlined" aria-hidden="true">filter_list</span>
      {t('dashboard.stations.filter')}
      {activeCount > 0 && (
        <span className="mobile-filter-badge" aria-hidden="true">{activeCount}</span>
      )}
    </button>
  );
};

/**
 * Desktop + mobile shell shared by Dashboard V2 list pages
 * (Sessions, Transactions, Users, ...). Pages provide the data-specific
 * slots (filter bar, table, cards, skeletons); the layout owns the
 * repeated structure: loading dim, error / skeleton / count / empty
 * branches, mobile header + search, filter button with badge,
 * card list, pagination, bottom nav, and the lazy filter sheet.
 */
export const ListPageLayout = ({
  section,
  filterBar,
  desktopToolbar,
  showLoading,
  isLoading,
  hasError,
  errorMessage,
  onRetry,
  countText,
  itemCount,
  emptyState,
  table,
  tableSkeleton,
  mobileTitle,
  mobileHeaderAction,
  mobileCountText,
  search,
  mobileCards,
  mobileSkeleton,
  pagination,
  filterButton,
  isFilterSheetOpen = false,
  filterSheet,
}: ListPageLayoutProps) => (
  <>
    {/* Desktop Layout */}
    <div className="desktop-layout">
      {section && <SectionTabs section={section} row />}
      {filterBar != null && (
        <div className={showLoading ? 'loading-dim' : ''}>{filterBar}</div>
      )}
      <div className="content-inner" style={{ padding: '2.5rem' }}>
        {desktopToolbar != null && (
          <div className={showLoading ? 'loading-dim' : ''}>{desktopToolbar}</div>
        )}
        <ListContent
          hasError={hasError}
          errorMessage={errorMessage}
          onRetry={onRetry}
          isLoading={isLoading}
          skeleton={
            <>
              {countText != null && (
                <div className="skeleton skeleton-count" style={{ marginBottom: '1.5rem' }} />
              )}
              {tableSkeleton}
            </>
          }
        >
          {countText != null && <p className="stations-count">{countText}</p>}
          {itemCount === 0 ? (
            <EmptyState icon={emptyState.icon} message={emptyState.message} />
          ) : (
            table
          )}
        </ListContent>
      </div>
    </div>

    {/* Mobile Layout */}
    <div className="mobile-layout">
      <MobileListHeader title={mobileTitle} action={mobileHeaderAction} search={search} />

      <main className="mobile-content">
        {section && <SectionTabs section={section} />}
        <div className="mobile-stations-header">
          <p className="mobile-stations-count">{mobileCountText}</p>
          {filterButton && (
            <MobileFilterButton
              activeCount={filterButton.activeCount}
              onOpen={filterButton.onOpen}
            />
          )}
        </div>

        <ListContent
          hasError={hasError}
          errorMessage={errorMessage}
          onRetry={onRetry}
          isLoading={isLoading}
          skeleton={mobileSkeleton}
        >
          {itemCount === 0 ? (
            <EmptyState icon={emptyState.icon} message={emptyState.message} />
          ) : (
            <div className="mobile-stations-list">{mobileCards}</div>
          )}
          {pagination != null && (
            <Pagination
              total={pagination.total}
              page={pagination.page}
              rowsPerPage={pagination.rowsPerPage}
              onPageChange={pagination.onPageChange}
              onRowsPerPageChange={pagination.onRowsPerPageChange}
              className="pagination-mobile"
            />
          )}
        </ListContent>
      </main>

      <MobileBottomNav />

      {isFilterSheetOpen && filterSheet != null && (
        <Suspense fallback={null}>{filterSheet}</Suspense>
      )}
    </div>
  </>
);

export default ListPageLayout;
